import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

export interface SubscriptionState {
  subscribed: boolean;
  isLifetime: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  plan: "monthly" | "yearly" | "starter" | "creator" | "pro" | "free" | null;
  planTier: "starter" | "creator" | "pro" | "free" | null;
  status: "active" | "inactive" | "canceled" | "past_due" | "trialing" | "refunded" | "free_trial" | null;
  stripeCustomerId: string | null;
  stripeSubscriptionId: string | null;
  hasRefund: boolean;
  refundMessage: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  subscription: SubscriptionState;
  isAdmin: boolean;
  checkSubscription: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const defaultSubscription: SubscriptionState = {
  subscribed: false,
  isLifetime: false,
  productId: null,
  subscriptionEnd: null,
  plan: null,
  planTier: null,
  status: null,
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  hasRefund: false,
  refundMessage: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionState>(defaultSubscription);

  const checkAdminRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Error checking admin role:", error);
        setIsAdmin(false);
        return;
      }

      setIsAdmin(!!data);
    } catch (error) {
      console.error("Error checking admin role:", error);
      setIsAdmin(false);
    }
  };

  const checkSubscription = async () => {
    try {
      // Check Stripe subscription first
      const { data, error } = await supabase.functions.invoke("check-subscription");
      
      if (error) {
        console.error("Error checking subscription:", error);
      }

      const isSubscribed = data?.subscribed ?? false;
      
      // If Stripe says subscribed, use that
      if (isSubscribed) {
        setSubscription({
          subscribed: true,
          isLifetime: data?.isLifetime ?? false,
          productId: data?.product_id ?? null,
          subscriptionEnd: data?.subscription_end ?? data?.current_period_end ?? null,
          plan: data?.plan ?? null,
          planTier: data?.plan_tier ?? null,
          status: data?.status ?? null,
          stripeCustomerId: data?.stripe_customer_id ?? null,
          stripeSubscriptionId: data?.stripe_subscription_id ?? null,
          hasRefund: data?.has_refund ?? false,
          refundMessage: data?.refund_message ?? null,
        });
        return;
      }

      // Check for refund status from Stripe
      if (data?.has_refund) {
        setSubscription({
          subscribed: false,
          isLifetime: false,
          productId: null,
          subscriptionEnd: null,
          plan: null,
          planTier: null,
          status: "refunded",
          stripeCustomerId: data?.stripe_customer_id ?? null,
          stripeSubscriptionId: null,
          hasRefund: true,
          refundMessage: data?.refund_message ?? null,
        });
        return;
      }

      // Fallback: check Whop membership
      try {
        const { data: whopData, error: whopError } = await supabase.functions.invoke("verify-whop-membership");
        
        if (!whopError && whopData?.has_whop && whopData?.membership_active && whopData?.plan_tier) {
          setSubscription({
            subscribed: true,
            isLifetime: false,
            productId: null,
            subscriptionEnd: null,
            plan: whopData.plan_tier,
            planTier: whopData.plan_tier,
            status: "active",
            stripeCustomerId: null,
            stripeSubscriptionId: null,
            hasRefund: false,
            refundMessage: null,
          });
          return;
        }
      } catch (whopErr) {
        console.error("Error checking Whop membership:", whopErr);
      }

      // No subscription from Stripe or Whop — check user_credits as fallback
      try {
        const { data: creditsData } = await supabase.functions.invoke("manage-credits", {
          body: { action: "check-balance" },
        });
        
        if (creditsData && creditsData.plan_tier && creditsData.plan_tier !== "free") {
          // User has a paid plan tier in user_credits (e.g. set by admin)
          setSubscription({
            subscribed: true,
            isLifetime: creditsData.is_unlimited ?? false,
            productId: null,
            subscriptionEnd: null,
            plan: creditsData.plan_tier,
            planTier: creditsData.plan_tier,
            status: "active",
            stripeCustomerId: data?.stripe_customer_id ?? null,
            stripeSubscriptionId: null,
            hasRefund: false,
            refundMessage: null,
          });
          return;
        }
      } catch (creditsErr) {
        console.error("Error checking credits fallback:", creditsErr);
      }

      // Truly no subscription from any source
      setSubscription({
        subscribed: false,
        isLifetime: false,
        productId: data?.product_id ?? null,
        subscriptionEnd: null,
        plan: "free",
        planTier: "free",
        status: "free_trial",
        stripeCustomerId: data?.stripe_customer_id ?? null,
        stripeSubscriptionId: null,
        hasRefund: false,
        refundMessage: null,
      });
    } catch (error) {
      console.error("Error checking subscription:", error);
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setIsAdmin(false);
    setSubscription(defaultSubscription);
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setIsLoading(false);

        // Check subscription status and admin role when user logs in
        if (session?.user) {
          setTimeout(() => {
            checkSubscription();
            checkAdminRole(session.user.id);
          }, 0);
        } else {
          setIsAdmin(false);
          setSubscription(defaultSubscription);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);

      if (session?.user) {
        checkSubscription();
        checkAdminRole(session.user.id);
      }
    });

    // Periodically check subscription status (every 60 seconds)
    const interval = setInterval(() => {
      if (user) {
        checkSubscription();
      }
    }, 60000);

    return () => {
      authSubscription.unsubscribe();
      clearInterval(interval);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, session, isLoading, subscription, isAdmin, checkSubscription, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
